
import {  required } from 'react-admin';

const startYear = 2020;

// Cache for school holidays to avoid repeated API calls
let schoolHolidaysCache = {};
let schoolHolidaysCacheTimestamp = {};
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

function formatDateMMDDYYYY(date) {
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
}

function isSchoolClosedType(type) {
    const normalized = (type || '').toString().trim().toLowerCase();
    return normalized === '' || normalized === 'holiday' || normalized === 'school break';
}

// Safe date parser for MM/DD/YYYY format (browser-independent)
function parseMMDDYYYY(dateStr) {
    if (!dateStr || typeof dateStr !== 'string') return null;
    const parts = dateStr.split('/');
    if (parts.length !== 3) return null;
    const mm = parseInt(parts[0], 10);
    const dd = parseInt(parts[1], 10);
    const yyyy = parseInt(parts[2], 10);
    if (isNaN(mm) || isNaN(dd) || isNaN(yyyy)) return null;
    return new Date(yyyy, mm - 1, dd);
}

function expandHolidayResponse(data) {
    if (Array.isArray(data?.holidayDetails) && data.holidayDetails.length > 0) {
        const expanded = [];
        data.holidayDetails.forEach((holiday) => {
            if (!isSchoolClosedType(holiday?.type)) {
                return;
            }
            const start = parseMMDDYYYY(holiday.startDate);
            if (!start) return; // Skip invalid dates
            const endRaw = holiday.endDate ? parseMMDDYYYY(holiday.endDate) : start;
            const end = (!endRaw || endRaw < start) ? start : endRaw;
            const iter = new Date(start);
            while (iter <= end) {
                expanded.push(formatDateMMDDYYYY(iter));
                iter.setDate(iter.getDate() + 1);
            }
        });
        return Array.from(new Set(expanded));
    }
    return Array.isArray(data?.holidays) ? data.holidays : [];
}

/**
 * Fetches school-specific holidays from the API
 * @param {string} schoolID - The school ID
 * @returns {Promise<string[]>} Array of holiday dates in MM/DD/YYYY format
 */
export async function fetchSchoolHolidays(schoolID) {
    if (!schoolID) {
        return []; // Return empty array if no schoolID
    }

    // Check cache first
    const now = Date.now();
    if (schoolHolidaysCache[schoolID] && schoolHolidaysCacheTimestamp[schoolID]) {
        const cacheAge = now - schoolHolidaysCacheTimestamp[schoolID];
        if (cacheAge < CACHE_DURATION_MS) {
            return schoolHolidaysCache[schoolID];
        }
    }

    try {
        const apiBaseURL = process.env.REACT_APP_GO_CONNECT_URL || '';
        // Use dedicated public endpoint for tour creation (no authentication required)
        const url = `${apiBaseURL}/api/schools/${encodeURIComponent(schoolID)}/0auth-info`;
        
        // Public endpoint - no authentication required
        const headers = {
            "Content-Type": "application/json",
        };

        const response = await fetch(url, {
            method: 'GET',
            headers: headers,
            mode: 'cors'
        });

        if (response.ok) {
            const data = await response.json();
            const holidays = expandHolidayResponse(data);
            
            // Update cache
            schoolHolidaysCache[schoolID] = holidays;
            schoolHolidaysCacheTimestamp[schoolID] = now;
            
            return holidays;
        } else {
            console.warn(`Failed to fetch holidays for school ${schoolID}:`, response.status);
            throw new Error(`Unable to verify holidays for school. Please try again.`);
        }
    } catch (error) {
        console.error(`Error fetching holidays for school ${schoolID}:`, error);
        throw new Error(`Unable to verify holidays for school. Please try again.`);
    }
}

/**
 * Creates a validation function for enrollment date that uses school-specific holidays
 * Note: This returns a function that uses cached holidays synchronously
 * @param {string} schoolID - The school ID
 * @param {string[]} holidays - Array of holiday dates in MM/DD/YYYY format
 * @returns {Function} Validation function
 */
export function createEnrollmentDateValidator(schoolID, holidays = []) {
    return (value) => {
        return checkEDtSync(value, holidays);
    };
}

/**
 * Synchronous version of checkEDt that uses provided holidays array
 * @param {string} value - Date value in YYYY-MM-DD format
 * @param {string[]} holidays - Array of holiday dates in MM/DD/YYYY format
 * @returns {string|undefined} Error message or undefined if valid
 */
function checkEDtSync(value, holidays = []) {
    if (!value) return undefined;
    // Date has to be in future and cannot be holiday or weekend
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    
    // Parse date string (YYYY-MM-DD) to avoid timezone issues
    // Split and create date in local timezone to prevent UTC interpretation
    const dateParts = value.split('-');
    if (dateParts.length !== 3) {
        return "Invalid date format";
    }
    const year = parseInt(dateParts[0], 10);
    const month = parseInt(dateParts[1], 10) - 1; // Month is 0-indexed
    const day = parseInt(dateParts[2], 10);
    var dt = new Date(year, month, day, 0, 0, 0, 0); // Create in local timezone

    // Check if the date is in the future
    if (dt <= today) {
        return "Enrollment Date has to be in future";
    }
    
    const dayOfWeek = dt.getDay();
    // Check if weekend (Saturday = 6, Sunday = 0)
    if (dayOfWeek === 6 || dayOfWeek === 0) {
        return "Enrollment cannot start on weekend";
    }
    
    // Check against provided holidays
    if (holidays && holidays.length > 0) {
        const formattedDate = convertDateFormat(value);
        
        if (holidays.includes(formattedDate)) {
            return "Enrollment Date cannot be a Holiday";
        }
    }
    
    // Return undefined if validation passes
    return undefined;
}

// Default validator (for backward compatibility, uses empty holidays)
export const validateEnrollmentDate = [required(), (value) => checkEDtSync(value, [])]


export function getYears() {
    var d = new Date(),
        year = d.getFullYear(),
        years = [];
        while (year >= startYear) {
            years.push({ id: year, name: year });
            year = year - 1;
        }
    // console.log("Years ", years);
    return years;
}

export function getCurrYr() {
    var d = new Date(),
    year = d.getFullYear();
    return year;
}

export function isAdmin(permissions) {
    if (permissions !== undefined && permissions.role === 'officemgr') {
        return true;
    }
    return false;
}

function convertDateFormat(inputDate) {
    // Split the input date string using "/"
    const dateParts = inputDate.split('-');

    // Rearrange the parts to match "yyyy-mm-dd" format
    const dd = dateParts[2];
    const yyyy = dateParts[0]; 
    const mm = dateParts[1]; 

    // Join the parts using "-"
    const outputDate = `${mm}/${dd}/${yyyy}`;

    return outputDate;
}

/**
 * Validates enrollment date against school-specific holidays and rules
 * @param {string} value - Date value in YYYY-MM-DD format
 * @param {string} schoolID - School ID for fetching school-specific holidays
 * @returns {Promise<string|undefined>} Error message or undefined if valid
 */
export async function checkEDt(value, schoolID = null) {
    // Date has to be in future and cannot be holiday or weekend
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    
    // Parse date string (YYYY-MM-DD) to avoid timezone issues
    // Split and create date in local timezone to prevent UTC interpretation
    const dateParts = value.split('-');
    if (dateParts.length !== 3) {
        return "Invalid date format";
    }
    const year = parseInt(dateParts[0], 10);
    const month = parseInt(dateParts[1], 10) - 1; // Month is 0-indexed
    const day = parseInt(dateParts[2], 10);
    var dt = new Date(year, month, day, 0, 0, 0, 0); // Create in local timezone

    // Check if the date is in the future
    if (dt <= today) {
        return "Enrollment Date has to be in future";
    }
    
    const dayOfWeek = dt.getDay();
    // Check if weekend (Saturday = 6, Sunday = 0)
    if (dayOfWeek === 6 || dayOfWeek === 0) {
        return "Enrollment cannot start on weekend";
    }
    
    // Check against school-specific holidays if schoolID is provided
    if (schoolID) {
        const holidays = await fetchSchoolHolidays(schoolID);
        const formattedDate = convertDateFormat(value);
        
        if (holidays.includes(formattedDate)) {
            return "Enrollment Date cannot be a Holiday";
        }
    }
    
    // Return undefined if validation passes
    return undefined;
}